import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { ArrowLeft, Shield, Lock, Check, AlertCircle, Loader2 } from "lucide-react";
import { api, setStoredToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Footer from "@/components/Footer";

/**
 * Login page — Option B (native Google OAuth).
 *
 * Users click the official Google "Sign in with Google" button and go DIRECTLY
 * to accounts.google.com — no intermediate auth.emergentagent.com hop.
 *
 * On success, the GoogleLogin component returns a Google ID token (credential)
 * which we POST to /api/auth/google. The backend verifies it server-side against
 * Google's public keys, finds-or-creates the user, and mints our session_token.
 */
export default function Login() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && user) navigate("/history", { replace: true });
  }, [user, loading, navigate]);

  const handleSuccess = async (credentialResponse) => {
    setBusy(true);
    setError("");
    try {
      const credential = credentialResponse?.credential;
      if (!credential) throw new Error("Google did not return a credential. Please try again.");
      const { data } = await api.post("/auth/google", { credential });
      // Persist Bearer fallback so 3rd-party-cookie-blocked browsers stay signed in
      if (data?.session_token) setStoredToken(data.session_token);
      await refresh();
      navigate("/history", { replace: true });
    } catch (e) {
      console.error("Google auth failed:", e);
      setError(
        e?.response?.data?.detail ||
        e?.message ||
        "Sign-in failed. Please try again or contact support."
      );
      setBusy(false);
    }
  };

  const handleError = () => {
    setError("Google sign-in was cancelled or blocked. If you have a pop-up blocker enabled, please disable it and try again.");
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50 text-zinc-900" data-testid="login-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 mb-10" data-testid="back-home">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="overline mb-4">◷ SIGN IN</div>
          <h1 className="font-display font-extrabold text-5xl lg:text-6xl tracking-tight leading-[1.0] mb-6">
            Save your audits.<br />
            Track your progress.
          </h1>
          <p className="text-zinc-600 text-lg max-w-md mb-10 leading-relaxed">
            Sign in with Google to keep your evaluation history, revisit AI rewrites,
            and watch your ResumeLab Score climb.
          </p>

          {/* Native Google sign-in button — renders Google's official button */}
          <div className="inline-block" data-testid="google-signin-wrapper">
            {busy ? (
              <div className="inline-flex items-center gap-3 px-6 py-3 bg-zinc-900 text-white text-sm font-medium" data-testid="login-busy">
                <Loader2 className="w-4 h-4 animate-spin" /> Signing you in…
              </div>
            ) : (
              <GoogleLogin
                onSuccess={handleSuccess}
                onError={handleError}
                size="large"
                text="continue_with"
                shape="rectangular"
                theme="outline"
                width="280"
              />
            )}
          </div>

          {error ? (
            <div className="mt-4 max-w-md p-3 border-l-2 border-[#EF4444] bg-red-50 text-xs text-red-800 flex items-start gap-2" data-testid="login-error">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          ) : null}

          {/* Trust note */}
          <p className="text-xs text-zinc-500 mt-5 max-w-md leading-relaxed flex items-start gap-2">
            <Lock className="w-3.5 h-3.5 text-zinc-400 mt-0.5 shrink-0" />
            <span>
              Signed in directly with Google.{" "}
              <strong className="text-zinc-700">Your password is never shared with ResumeLab.</strong>
            </span>
          </p>

          {/* Trust badges */}
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <span className="inline-flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-[#007A5E]" /> 256-BIT SSL</span>
            <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#007A5E]" /> GOOGLE OAUTH 2.0</span>
            <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#007A5E]" /> NO PASSWORD STORED</span>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="border border-zinc-200 p-8 lg:p-10 bg-zinc-50/60">
            <div className="overline mb-4">◆ WHAT YOU UNLOCK</div>
            <ul className="space-y-4">
              {[
                "Unlimited resume audits across roles",
                "Saved history with score trends",
                "AI rewrites you can re-generate any time",
                "Re-target the same resume to different JDs",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3 pb-4 border-b border-zinc-100 last:border-0">
                  <span className="font-mono text-xs text-zinc-400 mt-1">{String(i + 1).padStart(2, "0")}</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 pt-6 border-t border-zinc-200 text-xs text-zinc-500 leading-relaxed">
              Free first audit. <strong className="text-zinc-700">No credit card.</strong> Cancel anytime.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}
